/* eslint-disable @typescript-eslint/no-empty-function */
import { Roles } from 'ciam-commons';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Columns } from 'src/components/OrgAdminTable/types';
import { MyOrganisationTabs } from 'src/components/Pages/MyOrganisation/MyOrganisationPage';
import { Order, Settings as TableSettings } from 'src/components/Table/types';
import { getOrgUsers } from 'src/services/apiService';
import { OrgUser } from 'src/types';
import useAuth from './useAuth';

interface OrgAdminsState {
  admins: Array<OrgUser>;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  tableSettings: TableSettings;
  updateTableSettings: (settings: TableSettings) => void;
  updateSearchQuery: (searchText: string) => void;
}

const defaultOrgAdmins = {
  admins: [],
  isLoading: false,
  error: null,
  searchQuery: '',
  tableSettings: {
    order: Order.Asc,
    orderBy: Columns.FullName,
    page: 0,
    rowsPerPage: 15,
    count: 0,
  },
  updateTableSettings: (_value) => {
    throw new Error('<OrgAdminProvider> missing in a component tree');
  },
  updateSearchQuery: () => {
    throw new Error('<OrgAdminProvider> missing in a component tree');
  },
};
const OrgAdminsContext = createContext<OrgAdminsState>(defaultOrgAdmins);

const useOrgAdmins = (): OrgAdminsState => useContext(OrgAdminsContext);

export const OrgAdminsProvider: React.FC = ({ children }) => {
  const history = useHistory();

  const {
    user: { organisationId },
  } = useAuth();

  const [admins, setAdmins] = useState(defaultOrgAdmins.admins);
  const [isLoading, setIsLoading] = useState(defaultOrgAdmins.isLoading);
  const [error, setError] = useState(defaultOrgAdmins.error);
  const [searchQuery, setSearchQuery] = useState(() => {
    const sessionTableSettings = JSON.parse(
      sessionStorage.getItem('tableSettings')
    );
    if (sessionTableSettings?.tab === MyOrganisationTabs.Admins) {
      return sessionTableSettings.searchQuery;
    }
    return defaultOrgAdmins.searchQuery;
  });
  const [tableSettings, setTableSettings] = useState(() => {
    const sessionTableSettings = JSON.parse(
      sessionStorage.getItem('tableSettings')
    );
    if (sessionTableSettings?.tab === MyOrganisationTabs.Admins) {
      return sessionTableSettings.tableSettings;
    }
    return defaultOrgAdmins.tableSettings;
  });

  const updateTableSettings = ({
    order,
    orderBy,
    page,
    rowsPerPage,
    count,
  }: TableSettings) => {
    setTableSettings((prevState) => ({
      order: order ?? prevState.order,
      orderBy: (orderBy as Columns) ?? prevState.orderBy,
      page: page ?? prevState.page,
      rowsPerPage: rowsPerPage ?? prevState.rowsPerPage,
      count: count ?? prevState.count,
    }));
  };

  const updateSearchQuery = (value) => {
    setSearchQuery((prevState) => {
      if (prevState !== value) {
        updateTableSettings({ ...prevState, page: 0 });
      }
      return value;
    });
  };

  const fetchAdmins = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getOrgUsers(organisationId, {
        role: Roles.DELEGATED_ADMIN,
      });

      setAdmins(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    sessionStorage.setItem(
      'tableSettings',
      JSON.stringify({
        tab: MyOrganisationTabs.Admins,
        tableSettings,
        searchQuery,
      })
    );
  }, [tableSettings, searchQuery]);

  useEffect(() => {
    const prevOrgId = sessionStorage.getItem('orgId');
    if (organisationId !== prevOrgId || history.action === 'PUSH') {
      sessionStorage.removeItem('tableSettings');
      setTableSettings(defaultOrgAdmins.tableSettings);
    }
    fetchAdmins();
    sessionStorage.setItem('orgId', organisationId);

    return () => {
      setAdmins(defaultOrgAdmins.admins);
      setIsLoading(defaultOrgAdmins.isLoading);
      setError(defaultOrgAdmins.error);
    };
  }, [organisationId]);

  return (
    <OrgAdminsContext.Provider
      value={{
        admins,
        isLoading,
        error,
        searchQuery,
        tableSettings,
        updateSearchQuery,
        updateTableSettings,
      }}
    >
      {children}
    </OrgAdminsContext.Provider>
  );
};

export default useOrgAdmins;
